import { SDKStatusCode } from "@nebula-sdk/core";
import { NebulaWEBGameSDKAccount } from "./account";
import { NebulaWEBGameSDKCore } from "./core";
import { SDKBrower } from "./core/enum";
import { NebulaWEBGameSDKPayment } from "./payment";
import { NebulaWEBGameSDKView } from "./view";
import type { WEBSDKService } from "./type";
import type {
  SDKRequestGameCenterGiftArgs, SDKRequestGameCenterGiftResult, SDKAddShortcutResult, SDKCreateBannerAdArgs,
  SDKCreateBannerAdResult, SDKCreateCustomAdArgs, SDKCreateCustomAdResult, SDKCreateGameClubButtonArgs,
  SDKCreateGameClubButtonResult, SDKCreateInterstitialAdArgs, SDKCreateInterstitialAdResult, SDKCreateRewardedVideoAdArgs,
  SDKCreateRewardedVideoAdResult, SDKCreateSurveyArgs, SDKCreateSurveyResult, SDKOnShowResult, SDKHideShareMenuArgs,
  SDKHideShareMenuResult, SDKLoginResult, SDKNavigateToSidebarResult, SDKPayArgs, SDKOffShareAppMessageArgs,
  SDKOffShowResult, SDKOnShareAppMessageArgs, SDKPayResult, SDKOpenCustomerServiceConversationArgs, SDKOpenCustomerServiceConversationResult,
  SDKRequestSubscribeMessageArgs, SDKRequestSubscribeMessageResult, SDKShareAppMessageArgs, SDKShareAppMessageResult,
  SDKShowShareMenuArgs, SDKShowShareMenuResult, SDKOnShareAppMessageResult, SDKOnShowArgs, SDKInitArgs,
  SDKInitResult, SDKReportGameClubDataResult, SDKRequestGameClubTaskResult, SDKOffGameLiveTaskChangeArgs,
  SDKOffGameLiveTaskChangeResult, SDKOnGameLiveTaskChangeArgs, SDKOnGameLiveTaskChangeResult, SDKOnFeedStatusChangeArgs,
  SDKOnFeedStatusChangeResult, SDKOffFeedStatusChangeArgs, SDKOffFeedStatusChangeResult, SDKOpenGameLiveCollectionArgs,
  SDKOpenGameLiveCollectionResult, SDKReceiveGameLiveRewardArgs, SDKReceiveGameLiveRewardResult, SDKRequestGameLiveInfoResult,
  SDKRequestGameLiveTaskResult, SDKStartGameLiveArgs, SDKStartGameLiveResult, SDKRequestFeedInfoArgs, SDKRequestFeedInfoResult,
  SDKRequestFeedSubscribeArgs, SDKRequestFeedSubscribeResult, SDKRequestGameCenterTaskResult, SDKFinishGameCenterTaskArgs,
  SDKFinishGameCenterTaskResult, SDKReportActionLogArgs, SDKReportActionLogResult, SDKRequestChannelInfoResult,
  SDKCreateLoginQRCodeResult, SDKScanLoginQRCodeArgs, SDKScanLoginQRCodeResult, SDKPlugin, SDK, SDKOnAddToFavoritesArgs,
  SDKOffAddToFavoritesResult, SDKOnAddToFavoritesResult, SDKRuntime, SDKEnv, SDKPlatform, SDKOnShareTimelineArgs,
  SDKOnShareTimelineResult, SDKOffShareTimelineArgs, SDKOffShareTimelineResult, SDKOpenPrivacyContractResult,
  SDKOpenUserContractResult, SDKSetArgs, SDKSetResult,
} from "@nebula-sdk/core";

// Web平台小游戏SDK
export class NebulaWebGameSDK implements SDK {
  // 构造函数
  public constructor() {
    // 核心模块
    const core = new NebulaWEBGameSDKCore();
    // 账号模块
    const account = new NebulaWEBGameSDKAccount(core);
    // 支付模块
    const payment = new NebulaWEBGameSDKPayment(core, account);
    // 视图模块
    const view = new NebulaWEBGameSDKView({ core, account, payment });
    // 服务集合
    this.service = { core, account, payment, view };
  }

  // 服务实例
  protected readonly service: WEBSDKService;

  // 版本号
  public get version(): string {
    return this.service.core.version;
  }

  // 运行环境
  public get runtime(): SDKRuntime {
    return this.service.core.runtime;
  }

  // 环境变量
  public get env(): SDKEnv {
    return this.service.core.env;
  }

  // 平台信息
  public get platform(): SDKPlatform {
    return this.service.core.platform;
  }

  // 调试模式
  public get debug(): boolean {
    return this.service.core.debug;
  }

  // 调试模式
  public set debug(value: boolean) {
    this.service.core.debug = value;
  }

  // 游戏平台OpenId
  public get openId(): string {
    return this.service.core.openId;
  }

  // 游戏平台UnionId
  public get unionId(): string {
    return this.service.core.unionId;
  }

  // 账号凭证Token
  public get token(): string {
    return this.service.core.token;
  }

  // 环境支持
  public get support(): boolean {
    return this.service.core.support;
  }

  // 初始化
  public async init(args: SDKInitArgs): Promise<SDKInitResult> {
    if (typeof args.debug === "boolean") {
      this.debug = args.debug;
    }
    const result = await this.service.core.init(args);
    if (result.code === SDKStatusCode.NoSupport) return result;

    if (
      this.service.core.browser === SDKBrower.WeChat &&
      !this.service.account.authorize &&
      !import.meta.env.DEV
    ) {
      const query = this.service.core.parseJson(window.location.search.slice(1));
      if (query.code && query.state) {
        const { status, data } = await this.service.account.requestAuthorizeInfo({
          code: query.code,
          appId: query.state,
        });
        if (status === "error") {
          return {
            status: "error",
            code: SDKStatusCode.WechatAuthorizeError,
            msg: "微信授权失败",
            data: null,
          };
        }
        this.service.account.authorize = { appId: data!.appId, openId: data!.openId };
      } else {
        const { status, data } = await this.service.account.requestAuthorizeUrl();
        if (status === "error") {
          return {
            status: "error",
            code: SDKStatusCode.WechatAuthorizeError,
            msg: "微信授权失败",
            data: null,
          };
        }
        window.location.replace(data!.url);
        await new Promise<void>((resolve) => {
          setTimeout(() => {
            resolve();
          }, 1000);
        });
        return { status: "error", code: SDKStatusCode.Error, msg: "微信授权跳转中", data: null };
      }
    }

    const userInfoResult = await this.service.account.requestUserInfo();
    if (userInfoResult.status === "success") {
      this.service.core.userInfo = {
        openId: userInfoResult.data!.openId,
        unionId: String(userInfoResult.data!.unionId),
        token: this.service.core.token,
        scene: "",
        nbOpenId: userInfoResult.data!.openId,
        nbUnionId: String(userInfoResult.data!.unionId),
        firstLogin: false,
        firstRegisterTime: 0,
      };
    } else {
      this.service.core.userInfo = null;
    }

    const query = this.service.core.parseJson(window.location.search.slice(1));
    if (query.order_id && query.verify_token) {
      void this.service.view.callback({ orderNo: query.order_id, orderToken: query.verify_token });
    }

    void this.service.core.scopeRequestTrack({
      eventId: "init",
      eventName: result.status === "success" ? "初始化成功" : "初始化失败",
      eventGroup: "sdk",
      eventValue: { args, result: this.service.core.options },
      eventLevel: result.status === "success" ? "info" : "error",
    });

    return result;
  }

  // 登录
  public async login(): Promise<SDKLoginResult> {
    const result = await this.service.view.login();

    void this.service.core.scopeRequestTrack({
      eventId: "login",
      eventName: result.status === "success" ? "登录成功" : "登录失败",
      eventGroup: "sdk",
      eventValue: { args: null, result },
      eventLevel: result.status === "error" ? "error" : "info",
    });

    return result;
  }

  // 支付
  public async pay(args: SDKPayArgs): Promise<SDKPayResult> {
    const result = await this.service.view.pay(args);

    void this.service.core.scopeRequestTrack({
      eventId: "pay",
      eventName: result.status === "success" ? "支付成功" : "支付失败",
      eventGroup: "sdk",
      eventValue: { args, result },
      eventLevel: result.status === "error" ? "error" : "info",
    });

    return result;
  }

  // 创建游戏圈按钮
  public async createGameClubButton(
    args: SDKCreateGameClubButtonArgs,
  ): Promise<SDKCreateGameClubButtonResult> {
    return await this.service.core.createErrorResult(args, { gameClubButton: null });
  }

  // 查询游戏圈任务
  public async requestGameClubTask(): Promise<SDKRequestGameClubTaskResult> {
    return await this.service.core.createErrorResult(null, []);
  }

  // 上报游戏圈数据
  public async reportGameClubData(): Promise<SDKReportGameClubDataResult> {
    return await this.service.core.createErrorResult(null, null);
  }

  // 订阅消息
  public async requestSubscribeMessage(
    args: SDKRequestSubscribeMessageArgs,
  ): Promise<SDKRequestSubscribeMessageResult> {
    return await this.service.core.createErrorResult(args, []);
  }

  // 创建激励视频广告
  public async createRewardedVideoAd(
    args: SDKCreateRewardedVideoAdArgs,
  ): Promise<SDKCreateRewardedVideoAdResult> {
    return await this.service.core.createErrorResult(args, { rewardedVideoAd: null });
  }

  // 创建横幅广告
  public async createBannerAd(args: SDKCreateBannerAdArgs): Promise<SDKCreateBannerAdResult> {
    return await this.service.core.createErrorResult(args, { bannerAd: null });
  }

  // 创建插屏广告
  public async createInterstitialAd(
    args: SDKCreateInterstitialAdArgs,
  ): Promise<SDKCreateInterstitialAdResult> {
    return await this.service.core.createErrorResult(args, { interstitialAd: null });
  }

  // 创建原生广告
  public async createCustomAd(args: SDKCreateCustomAdArgs): Promise<SDKCreateCustomAdResult> {
    return await this.service.core.createErrorResult(args, { customAd: null });
  }

  // 创建问卷
  public async createSurvey(args: SDKCreateSurveyArgs): Promise<SDKCreateSurveyResult> {
    return await this.service.core.createErrorResult(args, { survey: null });
  }

  // 获取推荐流直玩信息
  public async requestFeedInfo(args: SDKRequestFeedInfoArgs): Promise<SDKRequestFeedInfoResult> {
    return await this.service.core.createErrorResult(args, null);
  }

  // 推荐流直玩订阅
  public async requestFeedSubscribe(
    args: SDKRequestFeedSubscribeArgs,
  ): Promise<SDKRequestFeedSubscribeResult> {
    return await this.service.core.createErrorResult(args, { success: false });
  }

  // 开启游戏直播
  public async startGameLive(args: SDKStartGameLiveArgs): Promise<SDKStartGameLiveResult> {
    return await this.service.core.createErrorResult(args, null);
  }

  // 获取游戏直播任务
  public async requestGameLiveTask(): Promise<SDKRequestGameLiveTaskResult> {
    return await this.service.core.createErrorResult(null, []);
  }

  // 获取游戏直播信息
  public async requestGameLiveInfo(): Promise<SDKRequestGameLiveInfoResult> {
    return await this.service.core.createErrorResult(null, null);
  }

  // 领取游戏直播奖励
  public async receiveGameLiveReward(
    args: SDKReceiveGameLiveRewardArgs,
  ): Promise<SDKReceiveGameLiveRewardResult> {
    return await this.service.core.createErrorResult(args, null);
  }

  // 打开游戏直播列表
  public async openGameLiveCollection(
    args: SDKOpenGameLiveCollectionArgs,
  ): Promise<SDKOpenGameLiveCollectionResult> {
    return await this.service.core.createErrorResult(args, null);
  }

  // 打开客服
  public async openCustomerServiceConversation(
    args: SDKOpenCustomerServiceConversationArgs,
  ): Promise<SDKOpenCustomerServiceConversationResult> {
    return await this.service.core.createErrorResult(args, null);
  }

  // 添加快捷方式
  public async addShortcut(): Promise<SDKAddShortcutResult> {
    return await this.service.core.createErrorResult(null, null);
  }

  // 获取游戏中心任务
  public async requestGameCenterTask(): Promise<SDKRequestGameCenterTaskResult> {
    return await this.service.core.createErrorResult(null, {
      addGameCenterToMyApps: { support: false, enable: false },
      revisitGameFromMyApps: { support: false, enable: false },
      addShortcutToSidebar: { support: false, enable: false },
      addShortcutToGameCenter: { support: false, enable: false },
      subscribeSecondFloorChannel: false,
      revisitSecondFloorChannel: false,
    });
  }

  // 完成游戏中心任务
  public async finishGameCenterTask(
    args: SDKFinishGameCenterTaskArgs,
  ): Promise<SDKFinishGameCenterTaskResult> {
    return await this.service.core.createErrorResult(args, null);
  }

  // 请求游戏中心礼包
  public async requestGameCenterGift(
    args: SDKRequestGameCenterGiftArgs,
  ): Promise<SDKRequestGameCenterGiftResult> {
    return this.service.core.createErrorResult(args, { download: null });
  }

  // 跳转侧边栏
  public async navigateToSidebar(): Promise<SDKNavigateToSidebarResult> {
    return await this.service.core.createErrorResult(null, null);
  }

  // 打开隐私协议页面
  public async openPrivacyContract(): Promise<SDKOpenPrivacyContractResult> {
    return await this.service.core.createErrorResult(null, null);
  }

  // 打开用户协议页面
  public async openUserContract(): Promise<SDKOpenUserContractResult> {
    return await this.service.core.createErrorResult(null, null);
  }

  // 显示分享菜单
  public async showShareMenu(args: SDKShowShareMenuArgs): Promise<SDKShowShareMenuResult> {
    return await this.service.core.createErrorResult(args, null);
  }

  // 隐藏分享菜单
  public async hideShareMenu(args: SDKHideShareMenuArgs): Promise<SDKHideShareMenuResult> {
    return await this.service.core.createErrorResult(args, null);
  }

  // 分享消息
  public async shareAppMessage(args: SDKShareAppMessageArgs): Promise<SDKShareAppMessageResult> {
    return await this.service.core.createErrorResult(args, null);
  }

  // 获取渠道信息
  public async requestChannelInfo(): Promise<SDKRequestChannelInfoResult> {
    return await this.service.core.createErrorResult(null, { tag: null });
  }

  // 生成二维码
  public async createLoginQRCode(): Promise<SDKCreateLoginQRCodeResult> {
    return this.service.core.createErrorResult(null, null);
  }

  // 扫描二维码
  public async scanLoginQRCode(args: SDKScanLoginQRCodeArgs): Promise<SDKScanLoginQRCodeResult> {
    return this.service.core.createErrorResult(args, null);
  }

  // 上报行为日志
  public async reportActionLog(args: SDKReportActionLogArgs): Promise<SDKReportActionLogResult> {
    return this.service.core.createErrorResult(args, null);
  }

  // 绑定回到前台的事件
  public async onShow(args: SDKOnShowArgs): Promise<SDKOnShowResult> {
    return await this.service.core.createSuccessResult(args, null);
  }

  // 解绑回到前台的事件
  public async offShow(args?: SDKOnShowArgs): Promise<SDKOffShowResult> {
    return await this.service.core.createSuccessResult(args, null);
  }

  // 绑定全局分享消息
  public async onShareAppMessage(
    args: SDKOnShareAppMessageArgs,
  ): Promise<SDKOnShareAppMessageResult> {
    return await this.service.core.createErrorResult(args, null);
  }

  // 解绑全局分享参数
  public async offShareAppMessage(
    args?: SDKOffShareAppMessageArgs,
  ): Promise<SDKOnShareAppMessageResult> {
    return await this.service.core.createErrorResult(args, null);
  }

  // 绑定分享到朋友圈
  public async onShareTimeline(args: SDKOnShareTimelineArgs): Promise<SDKOnShareTimelineResult> {
    return await this.service.core.createErrorResult(args, null);
  }

  // 解绑分享到朋友圈
  public async offShareTimeline(
    args?: SDKOffShareTimelineArgs,
  ): Promise<SDKOffShareTimelineResult> {
    return await this.service.core.createErrorResult(args, null);
  }

  // 绑定添加到收藏事件
  public async onAddToFavorites(args: SDKOnAddToFavoritesArgs): Promise<SDKOnAddToFavoritesResult> {
    return await this.service.core.createErrorResult(args, null);
  }

  // 解绑添加到收藏事件
  public async offAddToFavorites(): Promise<SDKOffAddToFavoritesResult> {
    return await this.service.core.createErrorResult(null, null);
  }

  // 绑定游戏直播任务变化
  public async onGameLiveTaskChange(
    args: SDKOnGameLiveTaskChangeArgs,
  ): Promise<SDKOnGameLiveTaskChangeResult> {
    return await this.service.core.createErrorResult(args, null);
  }

  // 解绑游戏直播任务变化
  public async offGameLiveTaskChange(
    args?: SDKOffGameLiveTaskChangeArgs,
  ): Promise<SDKOffGameLiveTaskChangeResult> {
    return await this.service.core.createErrorResult(args, null);
  }
  // 绑定推荐流直玩状态变化事件
  public async onFeedStatusChange(
    args: SDKOnFeedStatusChangeArgs,
  ): Promise<SDKOnFeedStatusChangeResult> {
    return this.service.core.createErrorResult(args, null);
  }

  // 解绑推荐流直玩状态变化事件
  public async offFeedStatusChange(
    args?: SDKOffFeedStatusChangeArgs,
  ): Promise<SDKOffFeedStatusChangeResult> {
    return this.service.core.createErrorResult(args, null);
  }
  // 全局设置
  public async set(args: SDKSetArgs): Promise<SDKSetResult> {
    return await this.service.core.createErrorResult(args, null);
  }

  // 集成扩展插件
  public use<T extends Array<unknown>>(plugin: SDKPlugin<T>, ...options: T): void;
  public use<T>(plugin: SDKPlugin<T>, options: T): void;
  public use(plugin: SDKPlugin, ...options: Array<unknown>): void {
    this.service.core.install(this, plugin, options);
  }
}

export * from "./account";
export * from "./core";
export * from "./payment";
export * from "./view";

export type * from "./type";
